import React, {Component, PropTypes} from 'react'
import {findDOMNode} from 'react-dom'
import moment from 'moment'
import Chartist from 'chartist'
import cx from 'classnames'
import style from './Chart.less'

export function totalAngle({source, angle, identify}) {
    const total = source.reduce((r, v) => r + identify(v), 0)
    return total * 360 / angle
}

export function plotData({serie, display, identify, value}) {
    const series = value.map((value) => {
        return serie(value).map((v) => ({x: display(v), y: identify(v)}))
    })

    return {series}
}

export function aggregateDataPie({display, identify, value}) {
    const data = value.reduce((result, value) => {
        result.labels.push(display(value))
        result.series.push(identify(value))
        return result
    }, {labels: [], series: []})

    return {
        ...data,
        labelInterpolationFnc: (v) => v || null
    }
}

export function aggregateDataTimeStep(options) {

    function toStepType(dateRange) {
        const timeframe = Math.abs(dateRange[0] - dateRange[1])

        if (timeframe < 14 * 24 * 3600000) return 'DAY'
        if (timeframe < 365 * 24 * 3600000) return 'MONTH'
        return 'YEAR'
    }

    function toStep(type, date, add = 0) {
        if (type == 'DAY') return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + add))
        if (type == 'MONTH') return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + add))
        return new Date(Date.UTC(date.getUTCFullYear() + add, 0))
    }

    function stepEquals(step1, step2) {
        if (!step1 || !step2) return false
        return step1.getUTCFullYear() == step2.getUTCFullYear() && step1.getUTCMonth() == step2.getUTCMonth() && step1.getUTCDate() == step2.getUTCDate()
    }

    function toStepLabel(type, step) {
        switch(type) {
            case 'DAY':
                return moment.utc(step).format('ddd')
            case 'MONTH':
                return moment.utc(step).format('MMM')
            default:
                return moment.utc(step).format('YYYY')
        }
    }

    return aggregateDataStep({toStepType, toStep, toStepLabel, stepEquals, fillSteps: true, ...options})
}

export function aggregateDataStep({toStepType, toStep, toStepLabel, stepEquals, stepReduce, fillSteps = false}) {
    toStepType = toStepType || (() => null)
    toStep = toStep || ((t, l) => l)
    toStepLabel = toStepLabel || ((t, s) => s)
    stepEquals = stepEquals || ((s1, s2) => s1 == s2)
    stepReduce = stepReduce || ((a) => a.reduce((r, v) => r + v, 0))

    return ({axisX, serie, display, identify, value}) => {

        function labelRangeReducer(result, value) {
            const seriez = serie(value)
            const startLabel = display(seriez[0])
            const endLabel = display(seriez[seriez.length - 1])

            result[0] = result[0] && startLabel > result[0] && result[0] || startLabel
            result[1] = result[1] && endLabel < result[1] && result[1] || endLabel
            return result
        }

        function stepAggregator(stepType) {
            return (result, value, index) => {
                return serie(value).reduce((result, entry) => {
                    const label = display(entry)
                    const value = identify(entry)
                    const step = toStep(stepType, label)

                    var aggrega = result.find((a) => stepEquals(a.step, step))
                    if (!aggrega) {
                        result.push(aggrega = {
                            step: step,
                            series: []
                        })
                    }

                    aggrega.series[index] = aggrega.series[index] || []
                    aggrega.series[index].push(value)
                    return result
                }, result)
            }
        }

        function stepComparator(aggrega1, aggrega2) {
            return aggrega1.step - aggrega2.step
        }

        function fillStepsReducer(stepType) {
            return (result, aggrega) => {
                if (fillSteps) {
                    const nextStep = aggrega.step
                    const lastAggrega = result[result.length - 1]
                    const lastStep = lastAggrega && lastAggrega.step || nextStep
                    var fillStep = toStep(stepType, lastStep, 1)

                    while (fillStep < nextStep) {
                        result.push({
                            step: fillStep,
                            series: []
                        })

                        fillStep = toStep(stepType, fillStep, 1)
                    }
                }

                result.push(aggrega)
                return result
            }
        }

        function dataReducer(serieCount) {
            return (result, aggrega) => {
                result.labels.push(aggrega.step)
                for(var i = 0 ; i < serieCount ; i++) {
                    const values = aggrega.series[i]
                    const average = values && stepReduce(values)
                    
                    result.series[i] = result.series[i] || []
                    result.series[i].push(average)
                }
                return result
            }
        }

        const stepType = toStepType(value.reduce(labelRangeReducer, []))
        const aggregations = value.reduce(stepAggregator(stepType), []).sort(stepComparator)
        const filledAgregations = aggregations.reduce(fillStepsReducer(stepType), [])
        const data = filledAgregations.reduce(dataReducer(value.length), {labels: [], series: []})

        return {
            ...data,
            axisX: {
                labelInterpolationFnc: (v) => toStepLabel(stepType, v),
                ...axisX
            }
        }
    }
}

function processData(options) {
    const {kind, axisX} = options

    if (kind == 'Pie') {
        return aggregateDataPie(options)
    }

    if (!axisX || !axisX.type || axisX.type == Chartist.StepAxis) {
        return aggregateDataStep({})(options)
    }

    return plotData(options)
}

class Chart extends Component {
    static propTypes = {
        kind: PropTypes.string,
        ratio: PropTypes.string,
        inverse: PropTypes.bool,
        brand: PropTypes.bool,
        data: PropTypes.func,
        serie: PropTypes.func,
        display: PropTypes.func,
        identify: PropTypes.func,
        onDraw: PropTypes.func,
        value: PropTypes.array.isRequired,
    }

    static defaultProps = {
        data: processData,
        serie: (s) => s,
        display: (v) => v,
        identify: (v) => v
    }

    constructor(props) {
        super(props)

        this.state = {
            labels: [],
            series: []
        }
    }

    componentWillMount() {
        const {data, ...options} = this.props
        const processData = (typeof data == 'function') && data || (() => data)

        const {labels, series, ...enhancedOptions} = processData(options)
        this.setState({labels, series, enhancedOptions})
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.value != this.props.value) {
            const {data, ...options} = nextProps
            const processData = (typeof data == 'function') && data || (() => data)

            const {labels, series, ...enhancedOptions} = processData(options)
            this.setState({labels, series, enhancedOptions})
        }
    }

    componentDidMount() {
        const {kind, ratio, inverse, brand, data, serie, display, identify, onDraw, value, responsive, ...options} = this.props
        const {labels, series, enhancedOptions} = this.state

        this._chart = new Chartist[kind](findDOMNode(this), {labels, series}, {...options, ...enhancedOptions}, responsive)

        if (onDraw) {
            this._chart.on('draw', onDraw)
        }
    }

    componentDidUpdate() {
        if (this._chart) {
            const {kind, ratio, inverse, brand, data, serie, display, identify, onDraw, value, responsive, ...options} = this.props
            const {labels, series, enhancedOptions} = this.state

            this._chart.update({labels, series}, {...options, ...enhancedOptions}, true)

            if (onDraw) {
                this._chart.on('draw', onDraw)
            }
        }
    }

    componentWillUnmount() {
        if (this._chart) {
            this._chart.detach()
        }
    }

    render() {
        const {ratio, fill, inverse, brand} = this.props
        const ratioClass = ratio && 'ct-' + ratio
        const fillClass = fill && 'ct-fill'
        const inverseClass = inverse && 'ct-inverse'
        const brandClass = brand && 'ct-brand'

        return (
            <div className={cx('ct-chart', ratioClass, fillClass, inverseClass, brandClass)} />
        )
    }
}

export default Chart


// WEBPACK FOOTER //
// ./lib/core/components/Chart.js